import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    TextField,
    IconButton,
    Rating,
    FormControlLabel,
    Checkbox,
    useMediaQuery,
    useTheme as useMuiTheme,
    Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Calendar } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import locale from 'date-fns/locale/en-US';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import CustomButton from '../Button/CustomButton';
import { useDispatch } from 'react-redux';
import { addCompanyReview } from '../../redux/company/actions';
import { ICompanyReview } from '../../redux/company/types';

interface ReviewFormData {
    review_subject: string;
    pros: string;
    cons: string;
    role: string;
    time: string;
    startDate: Date;
    endDate: Date;
    isCurrent: boolean;
    rating: number;
    professionalism: number;
    satisfaction: number;
    creative_quality: number;
    culture: number;
    payment_experience: number;
    reliability: number;
    recommend: number;
}

interface ReviewFormModalProps {
    open: boolean;
    onClose: () => void;
    companyId: string;
    companyName: string;
    editingReview?: ICompanyReview | null;
    onSubmit?: (formData: ReviewFormData) => void;
}

const ReviewFormModal: React.FC<ReviewFormModalProps> = ({
    open,
    onClose,
    companyId,
    companyName,
    editingReview,
    onSubmit,
}) => {
    const { themeStyle } = useThemeContext();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const dispatch = useDispatch();

    const [formData, setFormData] = useState<ReviewFormData>({
        review_subject: '',
        pros: '',
        cons: '',
        role: '',
        time: '',
        startDate: new Date(),
        endDate: new Date(),
        isCurrent: false,
        rating: 3,
        professionalism: 3,
        satisfaction: 3,
        creative_quality: 3,
        culture: 3,
        payment_experience: 3,
        reliability: 3,
        recommend: 3,
    });

    // Pre-populate form when editing
    useEffect(() => {
        if (editingReview && open) {
            setFormData({
                review_subject: editingReview.review_subject || '',
                pros: editingReview.pros || '',
                cons: editingReview.cons || '',
                role: editingReview.role || '',
                time: editingReview.time || '',
                startDate: new Date(), // Default to current date for editing
                endDate: new Date(), // Default to current date for editing
                isCurrent: editingReview.time === 'Current Employee',
                rating: editingReview.rating || 3,
                professionalism: 3, // Default values since these fields are not in ICompanyReview
                satisfaction: 3,
                creative_quality: 3,
                culture: 3,
                payment_experience: 3,
                reliability: 3,
                recommend: 3,
            });
        } else if (!editingReview && open) {
            // Reset form for new review
            setFormData({
                review_subject: '',
                pros: '',
                cons: '',
                role: '',
                time: '',
                startDate: new Date(),
                endDate: new Date(),
                isCurrent: false,
                rating: 3,
                professionalism: 3,
                satisfaction: 3,
                creative_quality: 3,
                culture: 3,
                payment_experience: 3,
                reliability: 3,
                recommend: 3,
            });
        }
    }, [editingReview, open]);

    const handleInputChange = (field: keyof ReviewFormData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleStartDateChange = (date: Date) => {
        setFormData(prev => ({
            ...prev,
            startDate: date,
        }));
    };

    const handleEndDateChange = (date: Date) => {
        setFormData(prev => ({
            ...prev,
            endDate: date,
        }));
    };

    const handleCurrentEmployeeChange = (isCurrent: boolean) => {
        setFormData(prev => ({
            ...prev,
            isCurrent,
            // If current employee, set endDate to 0 (epoch time)
            endDate: isCurrent ? new Date(0) : new Date(),
        }));
    };

    const handleSubmit = () => {
        // Calculate duration if not current
        let timeString = '';
        if (!formData.isCurrent) {
            const start = new Date(formData.startDate);
            const end = new Date(formData.endDate);
            const diffTime = Math.abs(end.getTime() - start.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            const months = Math.floor(diffDays / 30);
            const years = Math.floor(months / 12);

            if (years > 0) {
                timeString = `${years} year${years > 1 ? 's' : ''}`;
            } else if (months > 0) {
                timeString = `${months} month${months > 1 ? 's' : ''}`;
            } else {
                timeString = `${diffDays} day${diffDays > 1 ? 's' : ''}`;
            }
        } else {
            timeString = 'Current Employee';
        }

        if (editingReview && onSubmit) {
            // Call the parent's onSubmit for editing
            onSubmit(formData);
        } else {
            // Handle new review creation
            const submitData: any = {
                ...formData,
                review_text: formData.pros, // Save pros as review_text as well
                time: timeString,
                company: companyId,
                // If current employee, ensure endDate is 0
                endDate: formData.isCurrent ? new Date(0) : formData.endDate,
            };

            dispatch(addCompanyReview(submitData));
            onClose();
        }
    };

    const renderStarRating = (
        label: string,
        value: number,
        field: keyof ReviewFormData,
        onChange: (value: number) => void,
    ) => (
        <Box>
            <Typography
                sx={{
                    color: theme[themeStyle].tag_white,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    fontSize: '0.875rem',
                    mb: 1,
                }}>
                {label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Rating
                    value={value}
                    onChange={(_, newValue) => onChange(newValue || 3)}
                    precision={0.1}
                    max={5}
                    sx={{
                        '& .MuiRating-iconFilled': {
                            color: theme[themeStyle].highlight,
                        },
                        '& .MuiRating-iconHover': {
                            color: theme[themeStyle].highlight,
                        },
                        '& .MuiRating-iconEmpty': {
                            color: theme[themeStyle].tag_white,
                        },
                    }}
                />
                <Typography
                    sx={{
                        color: theme[themeStyle].highlight,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        minWidth: '40px',
                    }}>
                    {value.toFixed(1)}
                </Typography>
            </Box>
        </Box>
    );

    const calendarStyles = {
        '& .rdrCalendarWrapper, & .rdrDateRangeWrapper': {
            width: '100%',
        },
        '& .rdrMonth': {
            width: '100% !important',
        },
        '& .rdrDateDisplayWrapper': {
            backgroundColor: theme[themeStyle].secondaryBg,
        },
        '& .rdrCalendarWrapper': {
            backgroundColor: theme[themeStyle].secondaryBg,
            color: theme[themeStyle].tag_white,
        },
        '& .rdrDayDisabled': {
            backgroundColor: theme[themeStyle].darkGray60,
        },
        '& .rdrDateRangeWrapper': {
            backgroundColor: theme[themeStyle].secondaryBg,
            color: theme[themeStyle].tag_white,
        },
        '& .rdrDayNumber span': {
            color: theme[themeStyle].tag_white,
        },
        '& .rdrDayToday .rdrDayNumber span': {
            color: '#68d391',
        },
        '& .rdrSelected, & .rdrStartEdge, & .rdrEndEdge': {
            backgroundColor: '#63b3ed !important',
            color: '#ffffff !important',
        },
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: isMobile ? 0 : '50%',
                    left: isMobile ? 0 : '50%',
                    transform: isMobile ? 'none' : 'translate(-50%, -50%)',
                    width: isMobile ? '100%' : 600,
                    height: isMobile ? '100vh' : 'auto',
                    maxHeight: isMobile ? '100vh' : '90vh',
                    bgcolor: theme[themeStyle].pure_black,
                    boxShadow: 24,
                    borderRadius: isMobile ? 0 : '12px',
                    overflow: 'auto',
                    border: isMobile ? 'none' : `0.2px solid ${theme[themeStyle].tag_white}`,
                }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 3,
                        borderBottom: `0.5px solid ${theme[themeStyle].filters_white}`,
                        position: 'sticky',
                        top: 0,
                        bgcolor: theme[themeStyle].pure_black,
                        zIndex: 2,
                    }}>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        }}>
                        {editingReview ? 'Edit Review' : `Review ${companyName}`}
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: theme[themeStyle].tag_white }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Form Content */}
                <Box sx={{ p: 3 }}>
                    {/* Review Subject */}
                    <TextField
                        fullWidth
                        label="Review Subject"
                        value={formData.review_subject}
                        onChange={e => handleInputChange('review_subject', e.target.value)}
                        inputProps={{ maxLength: 50 }}
                        helperText={`${formData.review_subject.length}/50 characters`}
                        sx={{
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                                color: theme[themeStyle].tag_white,
                                '& fieldset': {
                                    borderColor: theme[themeStyle].filters_white,
                                },
                                '&:hover fieldset': {
                                    borderColor: theme[themeStyle].highlight,
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: theme[themeStyle].highlight,
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: theme[themeStyle].tag_white,
                                '&.Mui-focused': {
                                    color: theme[themeStyle].highlight,
                                },
                            },
                            '& .MuiFormHelperText-root': {
                                color: theme[themeStyle].tag_white,
                                opacity: 0.7,
                            },
                        }}
                    />

                    {/* Pros */}
                    <TextField
                        fullWidth
                        label="Pros *"
                        multiline
                        rows={4}
                        value={formData.pros}
                        onChange={e => handleInputChange('pros', e.target.value)}
                        inputProps={{ maxLength: 1000 }}
                        helperText={`${formData.pros.length}/1000 characters`}
                        required
                        sx={{
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                                color: theme[themeStyle].tag_white,
                                '& fieldset': {
                                    borderColor: theme[themeStyle].filters_white,
                                },
                                '&:hover fieldset': {
                                    borderColor: theme[themeStyle].highlight,
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: theme[themeStyle].highlight,
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: theme[themeStyle].tag_white,
                                '&.Mui-focused': {
                                    color: theme[themeStyle].highlight,
                                },
                            },
                            '& .MuiFormHelperText-root': {
                                color: theme[themeStyle].tag_white,
                                opacity: 0.7,
                            },
                        }}
                    />

                    {/* Cons */}
                    <TextField
                        fullWidth
                        label="Cons"
                        multiline
                        rows={4}
                        value={formData.cons}
                        onChange={e => handleInputChange('cons', e.target.value)}
                        inputProps={{ maxLength: 1000 }}
                        helperText={`${formData.cons.length}/1000 characters`}
                        sx={{
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                                color: theme[themeStyle].tag_white,
                                '& fieldset': {
                                    borderColor: theme[themeStyle].filters_white,
                                },
                                '&:hover fieldset': {
                                    borderColor: theme[themeStyle].highlight,
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: theme[themeStyle].highlight,
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: theme[themeStyle].tag_white,
                                '&.Mui-focused': {
                                    color: theme[themeStyle].highlight,
                                },
                            },
                            '& .MuiFormHelperText-root': {
                                color: theme[themeStyle].tag_white,
                                opacity: 0.7,
                            },
                        }}
                    />

                    {/* Role */}
                    <TextField
                        fullWidth
                        label="Role"
                        placeholder="e.g Lead Photographer, Content Strategist...e.t.c"
                        value={formData.role}
                        onChange={e => handleInputChange('role', e.target.value)}
                        inputProps={{ maxLength: 30 }}
                        helperText={`${formData.role.length}/30 characters`}
                        sx={{
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                                color: theme[themeStyle].tag_white,
                                '& fieldset': {
                                    borderColor: theme[themeStyle].filters_white,
                                },
                                '&:hover fieldset': {
                                    borderColor: theme[themeStyle].highlight,
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: theme[themeStyle].highlight,
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: theme[themeStyle].tag_white,
                                '&.Mui-focused': {
                                    color: theme[themeStyle].highlight,
                                },
                            },
                            '& .MuiFormHelperText-root': {
                                color: theme[themeStyle].tag_white,
                                opacity: 0.7,
                            },
                        }}
                    />

                    {/* Employment Period */}
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '0.875rem',
                                mb: 2,
                            }}>
                            Employment Period
                        </Typography>

                        {/* Start Date */}
                        <Box sx={{ mb: 2 }}>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '0.875rem',
                                    mb: 1,
                                }}>
                                Start Date
                            </Typography>
                            <Box sx={{ ...calendarStyles, width: '100%' }}>
                                <Calendar date={formData.startDate} onChange={handleStartDateChange} locale={locale} />
                            </Box>
                        </Box>

                        {/* Current Position Checkbox */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.isCurrent}
                                    onChange={e => handleCurrentEmployeeChange(e.target.checked)}
                                    sx={{
                                        color: theme[themeStyle].highlight,
                                        '&.Mui-checked': {
                                            color: theme[themeStyle].highlight,
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                    I currently work here
                                </Typography>
                            }
                            sx={{ mb: 2 }}
                        />

                        {/* End Date - Only show if not current */}
                        {!formData.isCurrent && (
                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: '0.875rem',
                                        mb: 1,
                                    }}>
                                    End Date
                                </Typography>
                                <Box sx={{ ...calendarStyles, width: '100%' }}>
                                    <Calendar date={formData.endDate} onChange={handleEndDateChange} locale={locale} />
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {/* Rating Fields */}
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '1.125rem',
                            mb: 2,
                        }}>
                        Ratings
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            {renderStarRating('Overall Rating', formData.rating, 'rating', value =>
                                handleInputChange('rating', value),
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {renderStarRating('Professionalism', formData.professionalism, 'professionalism', value =>
                                handleInputChange('professionalism', value),
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {renderStarRating('Satisfaction', formData.satisfaction, 'satisfaction', value =>
                                handleInputChange('satisfaction', value),
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {renderStarRating(
                                'Creative Quality',
                                formData.creative_quality,
                                'creative_quality',
                                value => handleInputChange('creative_quality', value),
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {renderStarRating('Culture', formData.culture, 'culture', value =>
                                handleInputChange('culture', value),
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {renderStarRating(
                                'Payment Experience',
                                formData.payment_experience,
                                'payment_experience',
                                value => handleInputChange('payment_experience', value),
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {renderStarRating('Reliability', formData.reliability, 'reliability', value =>
                                handleInputChange('reliability', value),
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {renderStarRating('Would Recommend', formData.recommend, 'recommend', value =>
                                handleInputChange('recommend', value),
                            )}
                        </Grid>
                    </Grid>

                    {/* Submit Button */}
                    <Box sx={{ mt: 4, mb: { xs: 6, sm: 0 } }}>
                        <CustomButton
                            label={editingReview ? 'Update Review' : 'Submit Review'}
                            onClick={handleSubmit}
                            customStyle={{ width: '100%' }}
                            sx={{
                                '&:hover': {
                                    WebkitTextFillColor: theme[themeStyle].primary_orange,
                                },
                            }}
                            disabled={!formData.pros.trim()}
                        />
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

export { ReviewFormModal };
